import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    IconButton,
    Tooltip,
    Paper,
    Divider,
    CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';

import { getRole, isSuperAdmin } from '../../auth';
import { list2FAAdmins, setup2FA, verify2FA, disable2FA } from '../../api';

/* ────── Setup Dialog ────── */

function SetupDialog({ email, onClose, onDone }) {
    const [step, setStep] = useState('loading'); // loading | scan | done
    const [qr, setQr] = useState('');
    const [secret, setSecret] = useState('');
    const [otp, setOtp] = useState('');
    const [copied, setCopied] = useState(false);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setup2FA(email)
            .then((data) => {
                setQr(data.qr);
                setSecret(data.secret);
                setStep('scan');
            })
            .catch((e) => {
                setErr(e?.message || 'Помилка генерації QR');
                setStep('scan');
            });
    }, [email]);

    function handleCopy() {
        navigator.clipboard.writeText(secret).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    async function handleVerify() {
        if (!otp || otp.length !== 6) return;
        setErr('');
        setBusy(true);
        try {
            await verify2FA(email, otp);
            setStep('done');
            onDone();
        } catch (e) {
            setErr(e?.message || 'Невірний OTP код');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open onClose={step === 'done' ? onClose : undefined} maxWidth="xs" fullWidth>
            <DialogTitle>Налаштувати 2FA — {email}</DialogTitle>

            <DialogContent>
                {step === 'loading' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={36} />
                    </Box>
                )}

                {step === 'scan' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {err && <Alert severity="error">{err}</Alert>}

                        {qr && (
                            <Box sx={{ textAlign: 'center' }}>
                                <img src={qr} alt="QR code" style={{ width: 200, height: 200 }} />
                            </Box>
                        )}

                        {secret && (
                            <>
                                <Typography variant="caption" color="text.secondary">
                                    Або введіть ключ вручну:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        background: '#f5f5f5',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 0.75,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}
                                    >
                                        {secret}
                                    </Typography>
                                    <Tooltip title={copied ? 'Скопійовано!' : 'Копіювати'}>
                                        <IconButton size="small" onClick={handleCopy}>
                                            {copied
                                                ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                                                : <ContentCopyIcon fontSize="small" />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </>
                        )}

                        <TextField
                            label="OTP код (6 цифр)"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                            size="small"
                            fullWidth
                            autoComplete="one-time-code"
                        />
                    </Box>
                )}

                {step === 'done' && (
                    <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                        2FA успішно налаштовано для {email}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                {step === 'done' ? (
                    <Button onClick={onClose} variant="contained">
                        Закрити
                    </Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={busy}>
                            Скасувати
                        </Button>
                        <Button
                            onClick={handleVerify}
                            variant="contained"
                            disabled={busy || otp.length !== 6}
                        >
                            {busy ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                            Підтвердити
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

/* ────── Disable Dialog ────── */

function DisableDialog({ email, onClose, onDone }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    async function handleDisable() {
        setErr('');
        setBusy(true);
        try {
            await disable2FA(email);
            onDone();
            onClose();
        } catch (e) {
            setErr(e?.message || 'Помилка');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Вимкнути 2FA</DialogTitle>
            <DialogContent>
                {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
                <Typography>
                    Ви впевнені, що хочете вимкнути 2FA для <strong>{email}</strong>?
                    Адмін не зможе увійти до налаштування нового коду.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>
                    Скасувати
                </Button>
                <Button onClick={handleDisable} color="error" variant="contained" disabled={busy}>
                    {busy ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    Вимкнути
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ────── Main Page ────── */

export default function SettingsPage() {
    const role = getRole();
    const superAdmin = isSuperAdmin();

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adminsErr, setAdminsErr] = useState('');

    const [setupEmail, setSetupEmail] = useState(null);
    const [disableEmail, setDisableEmail] = useState(null);

    const loadAdmins = useCallback(() => {
        if (!superAdmin) return;
        setLoading(true);
        setAdminsErr('');
        list2FAAdmins()
            .then(setAdmins)
            .catch((e) => setAdminsErr(e?.message || 'Помилка завантаження'))
            .finally(() => setLoading(false));
    }, [superAdmin]);

    useEffect(() => {
        loadAdmins();
    }, [loadAdmins]);

    return (
        <Box sx={{ maxWidth: 680, mx: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

            <Typography variant="h5" fontWeight={700}>
                Налаштування
            </Typography>

            {/* ── Блок Акаунт ── */}
            <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Акаунт
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography color="text.secondary">Роль:</Typography>
                    <Chip
                        label={superAdmin ? 'Superadmin' : 'Admin'}
                        color={superAdmin ? 'primary' : 'default'}
                        size="small"
                        icon={<SecurityIcon />}
                    />
                </Box>
            </Paper>

            {/* ── Суперадмін: Керування 2FA ── */}
            {superAdmin && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Керування 2FA адмінів
                    </Typography>

                    {adminsErr && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {adminsErr}
                        </Alert>
                    )}

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && admins.length === 0 && !adminsErr && (
                        <Typography color="text.secondary" variant="body2">
                            DB адміни відсутні. Створіть їх через{' '}
                            <code>node prisma/seed-admins.mjs</code>
                        </Typography>
                    )}

                    {!loading && admins.length > 0 && (
                        <List disablePadding>
                            {admins.map((admin, idx) => (
                                <React.Fragment key={admin.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItem disableGutters sx={{ py: 1 }}>
                                        <ListItemText
                                            primary={admin.email}
                                            secondary={
                                                admin.twoFactorEnabled
                                                    ? null
                                                    : '2FA не налаштовано'
                                            }
                                        />
                                        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {admin.twoFactorEnabled && (
                                                <Chip
                                                    label="2FA"
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                            {admin.twoFactorEnabled ? (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDisableEmail(admin.email)}
                                                >
                                                    Вимкнути
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => setSetupEmail(admin.email)}
                                                >
                                                    Налаштувати
                                                </Button>
                                            )}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Paper>
            )}

            {/* ── Звичайний адмін: Безпека ── */}
            {!superAdmin && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Безпека
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        2FA керує головний адміністратор
                    </Typography>
                </Paper>
            )}

            {/* ── Діалоги ── */}
            {setupEmail && (
                <SetupDialog
                    email={setupEmail}
                    onClose={() => setSetupEmail(null)}
                    onDone={loadAdmins}
                />
            )}

            {disableEmail && (
                <DisableDialog
                    email={disableEmail}
                    onClose={() => setDisableEmail(null)}
                    onDone={loadAdmins}
                />
            )}

        </Box>
    );
}
